import { Input, message, Modal } from 'antd';
import { UploadFile } from 'antd/lib/upload/interface';
import BgModalConsent from 'assets/images/driver-portal/bg-modal-consent.png';
import DriverIdCardPreviewImage from 'assets/images/driver-portal/preview/driver-id-card.png';
import DriverNameChangingPreviewImage from 'assets/images/driver-portal/preview/name-changing.png';
import { Button, FormItem, Text as TextComponent } from 'components';
import { theme } from 'configs/styled';
import Divider from 'containers/DriverPortalPage/components/Divider';
import DriverPortalLayout from 'containers/DriverPortalPage/components/DriverPortalLayout';
import InputCheckBox from 'containers/DriverPortalPage/components/InputCheckBox';
import Title from 'containers/DriverPortalPage/components/Title';
import HeaderPortal from 'containers/DriverPortalPage/MainPage/components/HeaderPortal';
import ReturnButton from 'containers/DriverPortalPage/MainPage/components/ReturnButton';
import UploadImageForm from 'containers/DriverPortalPage/MainPage/components/UploadImageForm';
import { useSession } from 'contexts/sessionContext';
import { FormikProps, FormikValues, getIn, useFormik } from 'formik';
import usePrompt from 'hooks/usePrompt';
import { useQueryParams } from 'hooks/useQueryParams';
import { useUpdateExpireDateImages } from 'hooks/useUpdateExpireDateImages';
import React, { useCallback, useEffect, useState } from 'react';
import {
  addressService,
  authenticationService,
  documentReaderService,
  mainService,
  uploadService,
} from 'services/driverPortal';
import { STATUS_RESPONSE } from 'services/driverPortal/DriverPortal.interface';
import { assertThaiId } from 'utils/validateThaiCitizen';
import { appendOcrMetadata } from 'utils/appendOcrMetadata';
import * as Yup from 'yup';
import Loading from '../../../../components/ReturnOrderModal/Loading';
import { ReaderType } from '../../../../services/driverPortal/documentReader/DocumentReaderService';
import { isPdfFile } from '../../../../utils/isFilePdf';
import { ASCII_DIGITS_ONLY, toIsoDate } from '../../../../utils/validate';
import ConfirmReturnModal from '../components/ConfirmReturnModal';
import Information from '../components/Information';
import { ISelectAddressProps } from '../PersonalInformationPage';

export interface IIdentityPageProps {}

interface IIdentityImage {
  image?: string;
  createdAt?: string;
  isAllowConsent?: boolean;
}
interface IIdentityRequest {
  driverPhotoDocument: IIdentityImage;
  citizenIDDocument: IIdentityImage;
  nameChangeDocument?: IIdentityImage;
  citizen: { id?: string };
  isAllowCriminalRecord?: boolean;
  status?: string;
  fileData?: any;
  readFile: boolean;
  error?: IError;
}

interface IError {
  iderror?: string;
  namechange?: string;
}

interface IIdentityImageUpload {
  driverPhotoDocument?: FormData;
  citizenIDDocument?: FormData;
  nameChangeDocument?: FormData;
}

enum ImageTypes {
  DRIVER_PHOTO_DOCUMENT = 'driverPhotoDocument',
  CITIZEN_ID_DOCUMENT = 'citizenIDDocument',
  NAME_CHANGE_DOCUMENT = 'nameChangeDocument',
}

const imageKeys: ImageTypes[] = [
  ImageTypes.DRIVER_PHOTO_DOCUMENT,
  ImageTypes.CITIZEN_ID_DOCUMENT,
  ImageTypes.NAME_CHANGE_DOCUMENT,
];

type ImageField = 'citizenIDDocument' | 'nameChangeDocument';

const folderType = 'identity';

const validateIdentity = Yup.object().shape({
  citizen: Yup.object().shape({
    id: Yup.string()
      .trim()
      .required('กรุณากรอกเลขบัตรประชาชน 13 หลัก')
      .matches(ASCII_DIGITS_ONLY, 'ไม่สามารถกรอกอักษรอื่นๆได้นอกจากตัวเลข')
      .length(13, 'กรุณากรอกเลขบัตรประชาชน 13 หลัก')
      .test(
        'is-validate-thai-citizenId',
        'รูปแบบของรหัสบัตรประชาชนไม่ถูกต้อง',
        (value: string) => assertThaiId(value),
      ),
  }),
  isAllowCriminalRecord: Yup.boolean().oneOf(
    [true],
    'กรุณาให้ความยินยอมการตรวจประวัติอาชญากรรม',
  ),
  citizenIDDocument: Yup.object().shape({
    image: Yup.string().required('กรุณาเลือกรูปภาพ'),
  }),
});

const IdentityPage: React.FC<IIdentityPageProps> = () => {
  const { userId, accessToken } = authenticationService.getUserToken();

  const [provinceOptions, setProvinceOptions] = useState<ISelectAddressProps[]>(
    [],
  );
  const [disableEdit, setDisableEdit] = useState(false);
  const [imageDeletionFiles, setImageDeletionFiles] = useState<UploadFile[]>(
    [],
  );
  const { updateByFiles } = useUpdateExpireDateImages();
  const [isShowModal, setIsShowModal] = useState(false);
  const { search, pushUrl } = useQueryParams();
  const [, setShowPrompt] = usePrompt();
  const [formDirty, setFormDirty] = useState(false);
  const [filesToUpload, setFilesToUpload] = useState<IIdentityImageUpload>({});
  const [consentModal, setConsentModal] = useState<boolean | undefined>(
    undefined,
  );
  const [saveLoading, setSaveLoading] = React.useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  const [idOcrLoading, setIdOcrLoading] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [citizenIdFileName, setCitizenIdFileName] = useState('');
  const [nameChangeFileName, setNameChangeFileName] = useState('');

  const { session } = useSession();

  const [allowCriminalRecordConsent, setAllowCriminalRecordConsent] = useState(
    false,
  );

  const [shouldDisplayButton, setShouldDisplayButton] = useState(false);

  const goToMainPage = () => {
    pushUrl('/', search);
  };

  const goToLoginPage = () => {
    pushUrl('/login', search);
  };

  const buildIdCardPayloadIdCard = async (values: IIdentityRequest) => {
    const f = values.fileData ?? {};

    console.log('===========file data', values);

    const identificationNumber =
      values.citizen?.id || f.identification_number || '';

    const fd = new FormData();
    fd.append('tab', 'id_card');
    fd.append('identificationNumber', identificationNumber ?? '');
    fd.append('thaiFullName', f.thai_fullname ?? '');
    fd.append('engFullName', f.english_fullname ?? '');
    fd.append('dateOfBirth', toIsoDate(f.date_of_birth) ?? '');
    fd.append('religion', f.religion ?? '');
    fd.append('address', f.address ?? '');
    fd.append(
      'shortAddress',
      (f.address ?? '').split(' ').slice(0, 2).join(' '),
    );
    fd.append('provinceId', f.province ?? '');
    fd.append('districtId', f.district ?? '');
    fd.append('subDistrictId', f.subDistrict ?? '');
    fd.append('postalCode', f.postCode ?? '');
    fd.append('dateOfIssue', toIsoDate(f.date_of_issue) ?? '');
    fd.append('dateOfExpiry', toIsoDate(f.date_of_expiry) ?? '');
    fd.append(
      'consentCriminalBackground',
      values.isAllowCriminalRecord ? '1' : '0',
    );
    appendOcrMetadata(fd, 'idCardOcrMetadata', f);

    if (filesToUpload.citizenIDDocument?.get('files')) {
      fd.append('idCard', filesToUpload.citizenIDDocument?.get('files') ?? '');
    }

    if (filesToUpload.nameChangeDocument?.get('files')) {
      fd.append(
        'certificateOfNameChange',
        filesToUpload.nameChangeDocument?.get('files') ?? '',
      );
    }

    console.log('========buidpaylaod', fd);

    return fd;
  };

  const formik = useFormik<IIdentityRequest>({
    initialValues: {
      error: {
        iderror: undefined,
        namechange: undefined,
      },
      driverPhotoDocument: {
        image: undefined,
        createdAt: undefined,
        isAllowConsent: false,
      },
      citizenIDDocument: {
        image: undefined,
        createdAt: undefined,
        isAllowConsent: false,
      },
      nameChangeDocument: {
        image: undefined,
        createdAt: undefined,
      },
      citizen: {
        id: '',
      },
      isAllowCriminalRecord: false,
      fileData: null,
      readFile: false,
    },
    validationSchema: validateIdentity,
    enableReinitialize: true,
    validateOnMount: true,

    onSubmit: async (formValues, action) => {
      if (saveLoading) return;
      setSaveLoading(true);
      try {
        setShowPrompt(false);

        const payload = await buildIdCardPayloadIdCard(formValues);

        if (payload instanceof FormData) {
          for (const [k, v] of payload.entries()) {
            console.log(
              k,
              v instanceof File ? `${v.name} (${v.type}, ${v.size}b)` : v,
            );
          }
        } else {
          console.log('payload', payload);
        }
        console.log('payload============', payload);

        const res = await addressService.updateInfo(payload);

        const out = (res as any)?.data ?? res;

        if (out?.success === false) {
          const msg =
            out?.errorMessageList?.[0] || out?.message || 'บันทึกไม่สำเร็จ';
          message.error(msg);
          return;
        }

        message.success('บันทึกข้อมูลเรียบร้อยแล้ว');
        goToMainPage();
      } catch (err) {
        const respData =
          err?.response?.data ??
          err?.body ??
          err?.data ??
          err?.result ??
          err?.payload ??
          null;

        const status =
          respData?.status ?? err?.response?.status ?? err?.status ?? 0;

        const detail =
          respData?.detail ??
          respData?.message ??
          (Array.isArray(respData?.errorMessageList)
            ? respData?.errorMessageList?.[0]
            : '') ??
          err?.message ??
          '';

        console.error('=== Update identity error:', err, {
          respData,
          status,
          detail,
        });

        const text = detail
          ? `Error ${status || 'Error'}: ${String(detail)}`
          : 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง';

        message.error(text);
        return;
      } finally {
        action?.setSubmitting?.(false);
        setSaveLoading(false);
      }
    },
  });

  const isSubmitDisabled =
    disableEdit ||
    idOcrLoading ||
    formik.isSubmitting ||
    formik.isValidating ||
    !formik.isValid;

  // const loadImage = async (path: string) => {
  //   try {
  //     const response = await mainService.loadImage(path);
  //     return response;
  //   } catch (e) {
  //     return null;
  //   }
  // };

  const loadImage = async (path: string) => {
    try {
      const response = await mainService.loadImageAxios(path);
      return response;
    } catch (e) {
      return null;
    }
  };

  const getUploadError = (fd?: {
    status?: boolean;
    message?: string;
    error_message?: string;
  }) => {
    if (!fd) return '';
    if (fd.status === false) {
      setFilesToUpload({
        ...filesToUpload,
        citizenIDDocument: undefined,
      });

      formik.setFieldValue('citizenIDDocument', {
        image: undefined,
      });

      return (
        fd.message?.trim() ||
        fd.error_message?.trim() ||
        'ไม่สามารถประมวลผลรูปภาพได้'
      );
    }
    return '';
  };

  useEffect(() => {
    const { accessToken } = authenticationService.getUserToken() || {};
    if (!accessToken) {
      pushUrl('/login', search);
    }
  }, [pushUrl, search]);

  const uploadImage = async (
    files: IIdentityImageUpload,
    key: ImageTypes,
    formValues: IIdentityRequest,
    createdAt: string,
  ) => {
    try {
      const uploadFile = files[key] || null;

      if (uploadFile) {
        const response = await uploadService.uploadFile(uploadFile);

        if (response.data) {
          formValues[key] = {
            image: response.data[0],
            createdAt,
          };
        }
      }
      return true;
    } catch (e) {
      return false;
    }
  };

  useEffect(() => {
    const err = getUploadError(formik.values.fileData);
    if (err) {
      formik.setFieldError('citizenIDDocument.image', err);
    } else {
      formik.setFieldError('citizenIDDocument.image', undefined as any);
    }
  }, [
    formik.values.fileData?.status,
    formik.values.fileData?.message,
    formik.values.fileData?.error_message,
  ]);

  const errors = formik.errors || {};

  const shouldDisplayErrorMessage = (key: keyof IIdentityRequest) => {
    return getIn(formik.touched, key) && getIn(formik.errors, key);
  };

  const getAddressDistricts = async (provinceId: string) => {
    try {
      return await addressService.districts({ provinceId });
    } catch (err) {
      return null;
    }
  };

  const getAddressSubDistricts = async (districtId: string) => {
    try {
      return await addressService.subDistricts({ districtId });
    } catch (err) {
      return null;
    }
  };

  const getPostCode = async (subDistrictId: string) => {
    try {
      return await addressService.postalCodes({ subDistrictId });
    } catch (err) {
      return null;
    }
  };

  const readerFile = async (file: File) => {
    try {
      if (file) {
        const response = await documentReaderService.readerFile(
          file,
          ReaderType.PERSONAL_ID,
        );

        if (response) {
          formik.setFieldValue('citizen', {
            id: (response.identification_number ?? '')
              .toString()
              .replaceAll(' ', ''),
          });

          const { province, district, sub_district } = response;

          if (provinceOptions) {
            const provinceFind = provinceOptions.find(
              f => f.title === province,
            );
            response.province = provinceFind?._id;

            if (provinceFind) {
              const districtsList = await getAddressDistricts(
                provinceFind?._id,
              );

              if (districtsList?.dataList) {
                const districtFind = districtsList?.dataList?.find(
                  f => f.district_name_th === district,
                );
                response.district = districtFind?.id;

                if (districtFind) {
                  const subDistrictsRes = await getAddressSubDistricts(
                    districtFind?.id?.toString(),
                  );

                  if (subDistrictsRes?.dataList) {
                    const subDistrictFind = subDistrictsRes?.dataList?.find(
                      f => f.sub_district_name_th.toString() == sub_district,
                    );
                    response.subDistrict = subDistrictFind?.id;

                    if (subDistrictFind) {
                      const postCodeRes = await getPostCode(
                        subDistrictFind?.id?.toString(),
                      );

                      if ((postCodeRes?.dataList?.length ?? 0) > 0) {
                        const postCodeFind = postCodeRes?.dataList[0];
                        response.postCode = postCodeFind?.zipcode;
                      }
                    }
                  }
                }
              }
            }
          }

          if (response.status) {
            formik.setFieldValue('fileData', response);
          } else {
            formik.setFieldValue('fileData', {});
            formik.setFieldValue('error', {
              iderror: 'ระบบไม่สามารถอัพโหลดรูปที่ไม่ใช่บัตรประชาชนได้',
            });
          }

          console.log('===== Debug response data: ', response);
        }
      }
    } catch (e) {
      console.log('===== Debug error status: ', e.toString());
    }
  };

  useEffect(() => {
    const { dirty, isSubmitting, isValid } = formik;
    const isDirty = !isSubmitting && dirty;

    setFormDirty(isDirty);
    setIsShowModal(isDirty);
    setShowPrompt(isDirty);

    setShouldDisplayButton(disableEdit || !isValid);
  }, [disableEdit, formik.isValid, formik.dirty, formik.isSubmitting]);

  const handleUnauthorized = useCallback(async status => {
    if (status === STATUS_RESPONSE.UNAUTHORIZED) {
      await authenticationService.logout();
      goToMainPage();
    }
  }, []);

  const removeDocument = useCallback(
    (field: ImageField, showErrorMessage = true) => (file?: UploadFile) => {
      if (file && !file.fileName?.startsWith('blob:')) {
        setImageDeletionFiles(prev => [...prev, file]);
      }

      const url: string | undefined = (formik.values as any)?.[field]?.image;
      if (url && url.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(url);
        } catch {}
      }

      setFilesToUpload(prev => ({ ...prev, [field]: undefined }));

      formik.setFieldValue(field, { image: undefined });
      formik.setFieldTouched(`${field}.image`, true, false);
      if (showErrorMessage) {
        formik.setFieldError(`${field}.image`, 'กรุณาเลือกรูปภาพ');
      } else {
        formik.setFieldError(`${field}.image`, undefined as any);
      }

      formik.setFieldValue('error', {
        iderror: undefined,
      });

      if (field === 'citizenIDDocument') {
        setIdOcrLoading(false);
        formik.setFieldValue('fileData', null);
      }
    },
    [formik],
  );

  const getAddressProvinces = async () => {
    // provinces
    const response = await addressService.provinces();
    const provinces = response;
    if (provinces) {
      const _provinces = provinces.dataList.map((p: any) => ({
        _id: p.id.toString(),
        title: p.province_name_th,
        value: p.province_name_th,
      }));
      setProvinceOptions(_provinces);
    }
  };

  const getInfo = async () => {
    try {
      const response = await mainService.getInfo();
      // const response = mainService.getMockPotentialLead(id);
      if (response.data.potentialLeadIdCard) {
        const infoData = response.data.potentialLeadIdCard;

        setIsApproved(infoData.statusCode === 'approved');

        formik.setFieldValue('citizen', {
          id: infoData?.identificationNumber ?? '',
        });
        const consent = !!infoData?.consentCriminalBackground;
        formik.setFieldValue('isAllowCriminalRecord', consent, false);
        formik.setFieldTouched('isAllowCriminalRecord', true, false);
        if (infoData?.idCard) {
          const image = await loadImage(infoData?.idCard);

          setCitizenIdFileName(infoData.idCard);
          formik.setFieldValue('fileData', {});
          formik.setFieldValue('citizenIDDocument', {
            image: image ? image.url : undefined,
          });
        } else {
          const imageNameChange = await loadImage(infoData?.image);

          formik.setFieldValue('nameChangeDocument', {
            image: imageNameChange ? imageNameChange.url : undefined,
          });
        }

        const nameChangeKey =
          (infoData as any)?.certificateOfNameChange ||
          (infoData as any)?.image;
        if (nameChangeKey) {
          const nc = await loadImage(nameChangeKey);

          setNameChangeFileName(nameChangeKey);
          formik.setFieldValue('nameChangeDocument', {
            image: nc ? nc.url : undefined,
          });
        }
      }
    } catch (e) {
      console.log('===== Debug error status: ', e.toString());
    }
  };

  useEffect(() => {
    let isMounted = true;

    const loadPage = async () => {
      try {
        await Promise.all([getInfo(), getAddressProvinces()]);
      } finally {
        if (isMounted) setPageLoading(false);
      }
    };

    loadPage();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleCloseModal = () => {
    setConsentModal(false);
  };

  return (
    <>
      {pageLoading && <Loading />}
      {formik.values.citizenIDDocument.image && !formik.values.fileData && (
        <Loading />
      )}

      <form onSubmit={formik.handleSubmit}>
        <HeaderPortal formik={formik as FormikProps<FormikValues>} />
        <DriverPortalLayout>
          <ReturnButton />

          <Information
            title="รูปถ่ายและบัตรประชาชน"
            description='กรุณากรอกข้อมูล และอัพโหลดเอกสารที่มีเครื่องหมาย * ให้ครบถ้วน แล้วกด"บันทึก"'
          />
          <FormItem
            required
            labelCol={{ span: 24 }}
            colon={false}
            validateStatus={
              errors?.citizenIDDocument?.image || formik.values.error?.iderror
                ? 'error'
                : ''
            }
            help={
              formik.values.error?.iderror ?? errors?.citizenIDDocument?.image
            }
          >
            <UploadImageForm
              disabled={disableEdit || isApproved}
              title="อัพโหลดรูปบัตรประจำตัวประชาชน (ด้านหน้า)"
              requiredTitle
              bulletList={[
                'บัตรประจำตัวประชาชนอยู่ในสภาพสมบูรณ์และ ไม่หมดอายุ',
                'ไม่แก้ไขและตกแต่งรูป',
                'เห็นข้อมูลชัดเจนและครบถ้วน',
                `กรุณาขีดทับข้อมูลหมู่เลือดและ/หรือข้อมูลศาสนาในรูปบัตรประชาชนของท่าน (ถ้ามี) หากท่านไม่ได้ขีดทับ ${
                  (session?.user?.vendorName != 'All Now'
                    ? session?.user?.vendorName
                    : 'บริษัท ออลล์ นาว ') || 'บริษัท ออลล์ นาว '
                } ขออนุญาตทำการขีดทับในข้อมูลดังกล่าว`,
              ]}
              previewTitle="ตัวอย่างรูปบัตรประจำตัวประชาชน"
              previewUrl={DriverIdCardPreviewImage}
              onChange={value => {
                const files = value.uploadFileForm
                  ? (value.uploadFileForm.get('files') as Blob)
                  : '';

                setFilesToUpload({
                  ...filesToUpload,
                  citizenIDDocument: value.uploadFileForm,
                });

                formik.setFieldValue('citizenIDDocument', {
                  image: files ? URL.createObjectURL(files) : undefined,
                });

                if (value.uploadFileForm?.get('files')) {
                  // @ts-ignore
                  readerFile(value.uploadFileForm.get('files'));
                }

                // console.log('====== Debug data: ', files, value.uploadFileForm);
              }}
              onRemove={removeDocument('citizenIDDocument', true)}
              nameImage="รูปบัตรประจำตัวประชาชน (ด้านหน้า)"
              pathImage={formik.values.citizenIDDocument.image}
              folder={folderType}
              isPdf={isPdfFile(citizenIdFileName)}
              fileName={citizenIdFileName}
            />
          </FormItem>

          <div style={{ marginTop: theme.spacing.lg }} />

          <FormItem
            label="เลขบัตรประชาชน 13 หลัก"
            required
            labelCol={{ span: 24 }}
            colon={false}
            validateStatus={
              shouldDisplayErrorMessage('citizen.id' as any) ? 'error' : ''
            }
            help={
              shouldDisplayErrorMessage('citizen.id' as any) &&
              errors?.citizen?.id
            }
          >
            <Input
              disabled={disableEdit || isApproved}
              size="large"
              name="citizen.id"
              placeholder="ตัวอย่าง 1234567890123"
              // onChange={formik.handleChange}
              onChange={e => {
                const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 13);
                formik.setFieldValue('citizen.id', v);
                formik.setFieldTouched('citizen.id', true, false);
              }}
              onBlur={formik.handleBlur}
              value={formik.values.citizen?.id}
              maxLength={13}
            />
          </FormItem>

          <Divider />

          <Title fontSize={theme.fontSize.md}>
            {'ยินยอมการตรวจประวัติอาชญากรรม'}
          </Title>

          <FormItem
            required
            labelCol={{ span: 24 }}
            colon={false}
            validateStatus={
              shouldDisplayErrorMessage('isAllowCriminalRecord') ? 'error' : ''
            }
            help={
              shouldDisplayErrorMessage('isAllowCriminalRecord') &&
              errors.isAllowCriminalRecord
            }
          >
            <InputCheckBox
              name="isAllowCriminalRecord"
              text={`ข้าพเจ้าให้ความยินยอมโดยชัดแจ้งแก่ ${
                (session?.user?.vendorName != 'All Now'
                  ? session?.user?.vendorName
                  : 'บริษัท ออลล์ นาว ') || 'บริษัท ออลล์ นาว '
              } (“บริษัทฯ”) ในการเก็บรวบรวม ใช้ เปิดเผยแก่กลุ่มบริษัท ออลล์ นาว และ/หรือลูกค้าของกลุ่มบริษัท ออลล์ นาว ได้แก่ บริษัท ซีพี ออลล์ จํากัด (มหาชน) บริษัท สยามแม็คโคร จำกัด (มหาชน) บริษัท เอก-ชัย ดีสทริบิวชั่น ซิสเทม จำกัด และ/หรือดำเนินการตามกระบวนการอื่นๆ ในการตรวจสอบประวัติอาชญากรรมของข้าพเจ้า สำหรับการพิจารณาคัดเลือกเป็นพนักงานขับรถ ผู้ช่วยพนักงานขับรถ หรือตำแหน่งอื่นใด ตามนโยบายของบริษัทฯ และ/หรือนโยบายลูกค้าของบริษัทฯ โดยมีระยะเวลาการจัดเก็บเป็นเวลา 2 ปีนับแต่สิ้นสภาพการจ้างงานของบริษัทฯ`}
              onChecked={() => {
                formik.setFieldValue(
                  'isAllowCriminalRecord',
                  !formik.values.isAllowCriminalRecord,
                );
                formik.setFieldTouched('isAllowCriminalRecord', true, false);
              }}
              disabled={disableEdit || isApproved}
              checked={formik.values.isAllowCriminalRecord}
            ></InputCheckBox>
          </FormItem>

          <FormItem
            labelCol={{ span: 24 }}
            colon={false}
            validateStatus={errors.nameChangeDocument ? 'error' : ''}
            help={errors.nameChangeDocument}
          >
            <UploadImageForm
              disabled={disableEdit || isApproved}
              title="อัพโหลดหนังสือสำคัญแสดงการจดทะเบียนเปลี่ยนชื่อสกุล"
              bulletList={['เห็นข้อมูลชัดเจนและครบถ้วน', 'เป็นภาพขาว-ดำได้']}
              previewTitle="ตัวอย่างหนังสือสำคัญแสดงการจดทะเบียนเปลี่ยนชื่อสกุล"
              previewUrl={DriverNameChangingPreviewImage}
              description="กรณีที่คุณมีการเปลี่ยนชื่อ และทำให้ชื่อในบัตร หรือเอกสารต่างๆ ไม่ตรงกัน คุณจำเป็นต้องอัพโหลด ”หนังสือสำคัญแสดงการจดทะเบียนเปลี่ยน ชื่อสกุล”"
              onChange={value => {
                const files = value.uploadFileForm
                  ? (value.uploadFileForm.get('files') as Blob)
                  : '';
                setFilesToUpload({
                  ...filesToUpload,
                  nameChangeDocument: value.uploadFileForm,
                });
                formik.setFieldValue('nameChangeDocument', {
                  image: files ? URL.createObjectURL(files) : undefined,
                });
              }}
              onRemove={removeDocument('nameChangeDocument', false)}
              nameImage="หนังสือสำคัญแสดงการจดทะเบียนเปลี่ยนชื่อสกุล"
              pathImage={formik.values.nameChangeDocument?.image}
              folder={folderType}
              isPdf={isPdfFile(nameChangeFileName)}
              fileName={nameChangeFileName}
            />
          </FormItem>
          <Divider />
          <Button
            disabled={isSubmitDisabled || isApproved}
            block
            size="large"
            type={shouldDisplayButton || isApproved ? 'default' : 'primary'}
            htmlType="submit"
            loading={saveLoading}
          >
            บันทึก
          </Button>

          <Modal
            visible={consentModal}
            footer={null}
            closeIcon={true}
            maskClosable={false}
            closable={false}
          >
            <div
              style={{
                width: '60%',
                position: 'relative',
                display: 'block',
                margin: '0 auto',
              }}
            >
              <img src={BgModalConsent} style={{ width: '100%' }} />
            </div>
            <div
              style={{
                marginTop: theme.spacing.xxl,
                marginBottom: theme.spacing.lg,
              }}
            >
              <Title
                style={{
                  fontFamily: 'Noto Sans, Noto Sans Thai',
                  fontSize: theme.fontSize.lg,
                }}
              >
                ท่านยังไม่ได้ให้ความยินยอม
              </Title>
              <TextComponent
                style={{
                  fontFamily: 'Noto Sans, Noto Sans Thai',
                  fontSize: theme.fontSize.sm,
                }}
              >
                การปฎิเสธไม่ให้ความยินยอมเรื่องการตรวจประวัติอาชญากรรมแก่บริษัทฯ
                ตามวัตถุประสงค์ข้างต้น
                อาจมีผลกระทบในการพิจารณาคัดเลือกเป็นพนักงานขับรถ
                ผู้ช่วยพนักงานขับรถหรือตำแหน่งอื่นใดตามนโยบายของบริษัทฯ
                และ/หรือนโยบายลูกค้าของบริษัทฯ
              </TextComponent>
            </div>
            <div style={{ textAlign: 'right' }}>
              <Button
                style={{
                  fontFamily: 'Noto Sans, Noto Sans Thai',
                  color: 'white',
                  background: '#1890FF',
                  border: '1px solid #1890FF',
                }}
                onClick={handleCloseModal}
              >
                ปิดหน้าต่างนี้
              </Button>
            </div>
          </Modal>
        </DriverPortalLayout>
        {formDirty && <ConfirmReturnModal isShow={isShowModal} />}
      </form>
    </>
  );
};

export default IdentityPage;
